import { useEffect, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { api } from '@/lib/api';
import type { OnboardingState } from '@pageboss/shared';
import type { LayerKey } from '@pageboss/shared';

export type UseOnboardingState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      state: OnboardingState | null;
      completeStep(step: LayerKey, skipped: boolean): Promise<OnboardingState>;
      finish(): Promise<OnboardingState>;
    }
  | { kind: 'error'; message: string };

export function useOnboarding(siteId: string): UseOnboardingState {
  // The Business half of the canonical route this screen is rendered under.
  // The Location half arrives as an argument; neither is ever inferred from
  // the other, and every fetch below is re-keyed on BOTH.
  const businessId = useBusinessId() ?? '';
  const [state, setState] = useState<UseOnboardingState>({ kind: 'loading' });

  useEffect(() => {
    // Both halves of the canonical route are required. Missing either is "no
    // Location in scope", not an empty onboarding state: stay in `loading`
    // and issue no request rather than inventing a tenant to ask about.
    if (!businessId || !siteId) return;
    let cancelled = false;
    setState({ kind: 'loading' });

    api
      .get<{ onboarding: OnboardingState | null }>(
        locationApi(businessId, siteId, 'onboarding'),
      )
      .then((res) => {
        if (cancelled) return;
        setState({
          kind: 'ready',
          state: res.onboarding,
          completeStep: async (step: LayerKey, skipped: boolean) => {
            const putRes = await api.put<{ onboarding: OnboardingState }>(
              locationApi(businessId, siteId, 'onboarding/step'),
              { step, skipped },
            );
            setState((prev) => {
              if (prev.kind !== 'ready') return prev;
              return { ...prev, state: putRes.onboarding };
            });
            return putRes.onboarding;
          },
          finish: async () => {
            const postRes = await api.post<{ onboarding: OnboardingState }>(
              locationApi(businessId, siteId, 'onboarding/complete'),
            );
            setState((prev) => {
              if (prev.kind !== 'ready') return prev;
              return { ...prev, state: postRes.onboarding };
            });
            return postRes.onboarding;
          },
        });
      })
      .catch((error) => {
        if (cancelled) return;
        const message =
          error instanceof Error
            ? error.message
            : "Couldn't load onboarding state right now";
        setState({ kind: 'error', message });
      });

    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  return state;
}
